import React, { useEffect, useState, useCallback } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, Chip, CircularProgress, IconButton, MenuItem, Stack, TableBody, TableCell, TableHead,
  TableRow, TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  AddRounded as AddIcon,
  AutorenewRounded as CaptureIcon,
  CloudUploadOutlined as UploadIcon,
  DeleteOutlined as DeleteIcon,
  PlayArrowRounded as RunIcon,
} from '@mui/icons-material';
import {
  BlockHeading, ConfirmDeleteDialog, DataTable, DatagridEmpty, PageTitle,
} from '@/components/shell';
import type { IOcrTestCase, IOcrTestExpectedField, IOcrTestImage, IOcrTestRunResult } from '@/types';
import { DocumentSide, FieldMatchMode, OcrTestResultStatus } from '@/types';
import { OCR_DUAL_SIDE_OPERATIONS } from '@/config/constants';
import { API_CONFIG } from '@/config/constants';
import { getToken } from '@/utils/auth';
import { IMPERSONATION_HEADER, getImpersonation } from '@/utils/impersonation';
import { HttpError } from '@/utils/httpClient';
import { ocrTestingService } from './ocrTestingService';
import { useOcrLabels } from './ocrLabels';
import { CaseInfoFields, type CaseInfo } from './CaseInfoFields';
import { FieldResultsTable } from './FieldResultsTable';
import { pickImage } from './pickImage';

const BACK_TO = '/ocr-testing/cases';
const MATCH_MODES = [FieldMatchMode.Exact, FieldMatchMode.Contains, FieldMatchMode.Regex, FieldMatchMode.NotEmpty];

/** The images of one side of the document, or the button to upload it. */
const ImageSide: React.FC<{
  label: string;
  images: IOcrTestImage[];
  urls: Record<string, string>;
  uploadLabel: string;
  onUpload: () => void;
  onDelete: (image: IOcrTestImage) => void;
}> = ({ label, images, urls, uploadLabel, onUpload, onDelete }) => {
  const t = useTranslate();
  const theme = useTheme();
  const frame = { border: `1px solid ${theme.palette.divider}`, borderRadius: theme.custom.radius.small };

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{label}</Typography>
      {images.length === 0 ? (
        <Button variant="outlined" startIcon={<UploadIcon />} onClick={onUpload}>{uploadLabel}</Button>
      ) : (
        <Stack spacing={1}>
          {images.map((img) => (
            <Stack key={img.id} spacing={1} alignItems="flex-start">
              {urls[img.id] ? (
                <Box component="img" src={urls[img.id]} alt={img.fileName} sx={{ maxWidth: '100%', maxHeight: 240, ...frame }} />
              ) : (
                <Box sx={{ width: '100%', height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', ...frame }}>
                  <CircularProgress size={20} aria-label={t('ra.page.loading')} />
                </Box>
              )}
              <Button variant="outlined" startIcon={<DeleteIcon />} onClick={() => onDelete(img)}>
                {t('screens.ocrTesting.deleteImage')}
              </Button>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export const TestCaseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const t = useTranslate();
  const notify = useNotify();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const labels = useOcrLabels();

  const [testCase, setTestCase] = useState<IOcrTestCase | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  /* Metadata form */
  const [info, setInfo] = useState<CaseInfo | null>(null);

  /* Expected fields */
  const [fields, setFields] = useState<IOcrTestExpectedField[]>([]);
  const [fieldsDirty, setFieldsDirty] = useState(false);
  const [savingFields, setSavingFields] = useState(false);
  const [capturingBaseline, setCapturingBaseline] = useState(false);
  const [baselineCaptured, setBaselineCaptured] = useState(false);

  /* Images */
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [imageToDelete, setImageToDelete] = useState<IOcrTestImage | null>(null);

  /* Single-case run */
  const [runningTest, setRunningTest] = useState(false);
  const [lastRunResult, setLastRunResult] = useState<IOcrTestRunResult | null>(null);

  const fetchCase = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await ocrTestingService.getCase(id);
      setTestCase(data);
      setInfo({
        name: data.name,
        description: data.description || '',
        documentTypeName: data.documentTypeName || '',
        nationalityCode: data.nationalityCode || '',
        operationType: data.operationType,
      });
      setFields(data.expectedFields || []);
      setFieldsDirty(false);
      setBaselineCaptured(false);

      // Load image URLs
      if (data.images?.length) {
        const urls: Record<string, string> = {};
        for (const img of data.images) {
          const endpoint = ocrTestingService.getImageUrl(data.id, img.id);
          const fullUrl = endpoint.startsWith('http') ? endpoint : `${API_CONFIG.BASE_URL}${endpoint}`;
          const token = getToken();
          const impersonation = getImpersonation();
          const res = await fetch(fullUrl, {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              ...(impersonation ? { [IMPERSONATION_HEADER]: impersonation.sub } : {}),
            },
          });
          if (res.ok) {
            const blob = await res.blob();
            urls[img.id] = URL.createObjectURL(blob);
          }
        }
        setImageUrls(urls);
      }
    } catch {
      notify('screens.ocrTesting.loadCaseFailed', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, notify]);

  useEffect(() => { fetchCase(); }, [fetchCase]);

  // Cleanup blob URLs
  useEffect(() => {
    return () => {
      Object.values(imageUrls).forEach(URL.revokeObjectURL);
    };
  }, [imageUrls]);

  const handleSaveMetadata = async () => {
    if (!id || !info || !info.name.trim()) return;
    setSaving(true);
    try {
      await ocrTestingService.updateCase(id, {
        name: info.name.trim(),
        description: info.description || undefined,
        documentTypeName: info.documentTypeName || undefined,
        nationalityCode: info.nationalityCode || undefined,
        operationType: info.operationType,
      });
      notify('screens.ocrTesting.caseUpdated', { type: 'success' });
    } catch {
      notify('screens.ocrTesting.caseUpdateFailed', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFields = async () => {
    if (!id) return;
    setSavingFields(true);
    try {
      await ocrTestingService.saveFields(id, fields);
      notify('screens.ocrTesting.fieldsSaved', { type: 'success' });
      setFieldsDirty(false);
    } catch {
      notify('screens.ocrTesting.fieldsSaveFailed', { type: 'error' });
    } finally {
      setSavingFields(false);
    }
  };

  const handleCaptureBaseline = async () => {
    if (!id) return;
    setCapturingBaseline(true);
    try {
      const { data: captured } = await ocrTestingService.captureBaseline(id);
      setFields(captured);
      setFieldsDirty(true);
      setBaselineCaptured(true);
      notify('screens.ocrTesting.baselineCaptured', { type: 'info' });
    } catch {
      notify('screens.ocrTesting.baselineFailed', { type: 'error' });
    } finally {
      setCapturingBaseline(false);
    }
  };

  const handleValidate = async () => {
    if (!id) return;
    try {
      await ocrTestingService.validateCase(id);
      notify('screens.ocrTesting.caseValidated', { type: 'success' });
      fetchCase();
    } catch {
      notify('screens.ocrTesting.validateFailed', { type: 'error' });
    }
  };

  const handleUploadImage = (side: DocumentSide) => {
    if (!id) return;
    pickImage(async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('side', String(side));
      try {
        await ocrTestingService.uploadImage(id, formData);
        notify('screens.ocrTesting.imageUploaded', { type: 'success' });
        fetchCase();
      } catch {
        notify('screens.ocrTesting.imageUploadFailed', { type: 'error' });
      }
    });
  };

  /* Field editing helpers */
  const updateField = <K extends keyof IOcrTestExpectedField>(index: number, key: K, value: IOcrTestExpectedField[K]) => {
    setFields(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [key]: value };
      return copy;
    });
    setFieldsDirty(true);
  };

  const addField = () => {
    setFields(prev => [...prev, { fieldName: '', expectedValue: '', matchMode: FieldMatchMode.Exact }]);
    setFieldsDirty(true);
  };

  const removeField = (index: number) => {
    setFields(prev => prev.filter((_, i) => i !== index));
    setFieldsDirty(true);
  };

  const handleRunSingle = async () => {
    if (!id) return;
    setRunningTest(true);
    setLastRunResult(null);
    try {
      // Run all (filtered by this case's operation type) and then get results for this case
      const { data: run } = await ocrTestingService.runAll(testCase?.operationType);
      // Poll until finished
      let finished = false;
      let attempts = 0;
      while (!finished && attempts < 60) {
        await new Promise(r => setTimeout(r, 3000));
        const { data: updated } = await ocrTestingService.getRun(run.id);
        if (updated.finishedAt) finished = true;
        attempts++;
      }
      // Get results and find this case
      const { data: results } = await ocrTestingService.getRunResults(run.id);
      const resultList = Array.isArray(results) ? results : [];
      const myResult = resultList.find(r => r.testCaseId === id);
      if (myResult) {
        setLastRunResult(myResult);
        notify(
          myResult.status === OcrTestResultStatus.Passed
            ? 'screens.ocrTesting.testPassed'
            : myResult.status === OcrTestResultStatus.Failed
              ? 'screens.ocrTesting.testFailed'
              : 'screens.ocrTesting.testError',
          { type: myResult.status === OcrTestResultStatus.Passed ? 'success' : 'error' }
        );
      } else {
        notify('screens.ocrTesting.noResultForCase', { type: 'warning' });
      }
    } catch (e) {
      if (e instanceof HttpError && e.status === 409) notify('screens.ocrTesting.runAlreadyInProgress', { type: 'warning' });
      else notify('screens.ocrTesting.runTestFailed', { type: 'error' });
    } finally {
      setRunningTest(false);
    }
  };

  /** Update expected fields from the actual OCR results */
  const handleUpdateFieldsFromResults = () => {
    if (!lastRunResult?.fieldResults) return;
    const updated: IOcrTestExpectedField[] = lastRunResult.fieldResults.map(fr => ({
      fieldName: fr.fieldName,
      expectedValue: fr.actualValue ?? fr.expectedValue,
      matchMode: fr.matchMode,
    }));
    setFields(updated);
    setFieldsDirty(true);
    notify('screens.ocrTesting.fieldsFromResults', { type: 'info' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress aria-label={t('ra.page.loading')} />
      </Box>
    );
  }

  if (!testCase || !info) {
    return (
      <Stack spacing={2} sx={{ pt: 1.5 }}>
        <PageTitle title={t('screens.ocrTesting.casesTitle')} backTo={BACK_TO} />
        <Card><DatagridEmpty title={t('screens.ocrTesting.caseNotFound')} hint={t('screens.ocrTesting.caseNotFoundHint')} /></Card>
      </Stack>
    );
  }

  const isDualSide = OCR_DUAL_SIDE_OPERATIONS.includes(info.operationType);
  const frontImages = testCase.images?.filter(i => i.side === DocumentSide.Front) || [];
  const backImages = testCase.images?.filter(i => i.side === DocumentSide.Back) || [];
  const matchedCount = lastRunResult?.fieldResults?.filter(f => f.matched).length ?? 0;

  const removeFieldButton = (idx: number) => (
    <Tooltip title={t('screens.ocrTesting.removeField')}>
      <IconButton aria-label={t('screens.ocrTesting.removeField')} onClick={() => removeField(idx)}>
        <DeleteIcon />
      </IconButton>
    </Tooltip>
  );

  const matchModeSelect = (field: IOcrTestExpectedField, idx: number, variant: 'standard' | 'outlined') => (
    <TextField
      select
      fullWidth
      variant={variant}
      label={variant === 'outlined' ? t('screens.ocrTesting.mode') : undefined}
      inputProps={variant === 'standard' ? { 'aria-label': t('screens.ocrTesting.mode') } : undefined}
      value={field.matchMode}
      onChange={(e) => updateField(idx, 'matchMode', Number(e.target.value) as FieldMatchMode)}
    >
      {MATCH_MODES.map((mode) => <MenuItem key={mode} value={mode}>{labels.matchMode(mode)}</MenuItem>)}
    </TextField>
  );

  const noFields = <DatagridEmpty title={t('screens.ocrTesting.noFields')} hint={t('screens.ocrTesting.noFieldsHint')} />;

  return (
    <Stack spacing={3} sx={{ pt: 1.5, pb: 3 }}>
      <PageTitle
        title={testCase.name}
        adornment={(
          <Chip
            label={t(testCase.isValidated ? 'screens.ocrTesting.validated' : 'screens.ocrTesting.draft')}
            color={testCase.isValidated ? 'success' : 'warning'}
            size="small"
          />
        )}
        backTo={BACK_TO}
        actions={(
          <Tooltip title={testCase.isValidated ? '' : t('screens.ocrTesting.runNeedsValidation')}>
            <span>
              <Button
                variant="outlined"
                startIcon={runningTest ? <CircularProgress size={16} color="inherit" /> : <RunIcon />}
                onClick={handleRunSingle}
                disabled={runningTest || !testCase.isValidated}
              >
                {runningTest ? t('screens.ocrTesting.runningTest') : t('screens.ocrTesting.runTest')}
              </Button>
            </span>
          </Tooltip>
        )}
      />

      <Box component="section" aria-labelledby="case-info">
        <BlockHeading
          id="case-info"
          title={t('screens.ocrTesting.info')}
          action={(
            <Button variant="contained" onClick={handleSaveMetadata} disabled={saving}>
              {saving ? t('screens.ocrTesting.saving') : t('ra.action.save')}
            </Button>
          )}
        />
        <Card sx={{ p: { xs: 2, sm: 3 } }}>
          <CaseInfoFields value={info} onChange={(patch) => setInfo((prev) => (prev ? { ...prev, ...patch } : prev))} />
        </Card>
      </Box>

      <Box component="section" aria-labelledby="case-images">
        <BlockHeading id="case-images" title={t('screens.ocrTesting.images')} />
        <Card sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: isDualSide ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)' } }}>
            <ImageSide
              label={t('screens.ocrTesting.front')}
              images={frontImages}
              urls={imageUrls}
              uploadLabel={t('screens.ocrTesting.uploadFront')}
              onUpload={() => handleUploadImage(DocumentSide.Front)}
              onDelete={setImageToDelete}
            />
            {isDualSide && (
              <ImageSide
                label={t('screens.ocrTesting.back')}
                images={backImages}
                urls={imageUrls}
                uploadLabel={t('screens.ocrTesting.uploadBack')}
                onUpload={() => handleUploadImage(DocumentSide.Back)}
                onDelete={setImageToDelete}
              />
            )}
          </Box>
        </Card>
      </Box>

      <Box component="section" aria-labelledby="case-fields">
        <BlockHeading
          id="case-fields"
          title={t('screens.ocrTesting.expectedFields')}
          action={(
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={capturingBaseline ? <CircularProgress size={16} color="inherit" /> : <CaptureIcon />}
                onClick={handleCaptureBaseline}
                disabled={capturingBaseline}
              >
                {capturingBaseline ? t('screens.ocrTesting.capturing') : t('screens.ocrTesting.captureBaseline')}
              </Button>
              <Button variant="outlined" onClick={handleValidate} disabled={fields.length === 0 || testCase.isValidated}>
                {t('screens.ocrTesting.validate')}
              </Button>
              <Button variant="contained" onClick={handleSaveFields} disabled={savingFields || !fieldsDirty}>
                {savingFields ? t('screens.ocrTesting.saving') : t('screens.ocrTesting.saveFields')}
              </Button>
            </Stack>
          )}
        />
        <Stack spacing={1.5}>
          {baselineCaptured && <Alert severity="warning">{t('screens.ocrTesting.baselineWarning')}</Alert>}

          {isPhone ? (
            <Stack spacing={1}>
              {fields.length === 0 && <Card>{noFields}</Card>}
              {fields.map((field, idx) => (
                <Card key={idx} sx={{ p: 1.5 }}>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <TextField
                        fullWidth
                        label={t('screens.ocrTesting.fieldName')}
                        value={field.fieldName}
                        onChange={(e) => updateField(idx, 'fieldName', e.target.value)}
                      />
                      {removeFieldButton(idx)}
                    </Box>
                    <TextField
                      fullWidth
                      label={t('screens.ocrTesting.expectedValue')}
                      value={field.expectedValue}
                      onChange={(e) => updateField(idx, 'expectedValue', e.target.value)}
                    />
                    {matchModeSelect(field, idx, 'outlined')}
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <DataTable label={t('screens.ocrTesting.expectedFields')} empty={fields.length === 0 ? noFields : undefined}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('screens.ocrTesting.field')}</TableCell>
                  <TableCell>{t('screens.ocrTesting.expectedValue')}</TableCell>
                  <TableCell sx={{ width: 190 }}>{t('screens.ocrTesting.mode')}</TableCell>
                  <TableCell sx={{ width: 56 }} aria-label={t('screens.ocrTesting.removeField')} />
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((field, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <TextField
                        variant="standard"
                        fullWidth
                        value={field.fieldName}
                        onChange={(e) => updateField(idx, 'fieldName', e.target.value)}
                        placeholder={t('screens.ocrTesting.fieldName')}
                        inputProps={{ 'aria-label': t('screens.ocrTesting.fieldName') }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="standard"
                        fullWidth
                        value={field.expectedValue}
                        onChange={(e) => updateField(idx, 'expectedValue', e.target.value)}
                        placeholder={t('screens.ocrTesting.expectedValue')}
                        inputProps={{ 'aria-label': t('screens.ocrTesting.expectedValue') }}
                      />
                    </TableCell>
                    <TableCell>{matchModeSelect(field, idx, 'standard')}</TableCell>
                    <TableCell align="center">{removeFieldButton(idx)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </DataTable>
          )}

          <Box>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={addField}>
              {t('screens.ocrTesting.addField')}
            </Button>
          </Box>
        </Stack>
      </Box>

      {lastRunResult && (
        <Box component="section" aria-labelledby="case-last-result">
          <BlockHeading
            id="case-last-result"
            title={t('screens.ocrTesting.lastResult', { status: labels.resultStatus(lastRunResult.status) })}
            subtitle={[
              t('screens.ocrTesting.timeValue', { ms: lastRunResult.responseTimeMs }),
              lastRunResult.fieldResults
                ? t('screens.ocrTesting.fieldsMatched', { matched: matchedCount, total: lastRunResult.fieldResults.length })
                : null,
            ].filter(Boolean).join(' · ')}
            action={lastRunResult.status === OcrTestResultStatus.Failed && lastRunResult.fieldResults ? (
              <Button variant="outlined" onClick={handleUpdateFieldsFromResults}>
                {t('screens.ocrTesting.fixFields')}
              </Button>
            ) : undefined}
          />
          <Stack spacing={1.5}>
            {lastRunResult.errorMessage && <Alert severity="error">{lastRunResult.errorMessage}</Alert>}
            {lastRunResult.fieldResults && lastRunResult.fieldResults.length > 0 && (
              isPhone
                ? <Card sx={{ p: 1.5 }}><FieldResultsTable results={lastRunResult.fieldResults} /></Card>
                : <FieldResultsTable results={lastRunResult.fieldResults} />
            )}
          </Stack>
        </Box>
      )}

      <ConfirmDeleteDialog
        open={Boolean(imageToDelete)}
        onClose={() => setImageToDelete(null)}
        name={imageToDelete?.fileName ?? ''}
        onDelete={async () => {
          if (!id || !imageToDelete) return;
          await ocrTestingService.deleteImage(id, imageToDelete.id);
          fetchCase();
        }}
      />
    </Stack>
  );
};
